import { ICitation } from '@vivli/shared/features/citations/infrastructure/interface';
import { useCitation } from '@vivli/shared/features/citations/infrastructure/hook';
import { Color, Size } from '@vivli/shared/theme';
import { CSSProperties } from 'react';
import { useToastApiContext } from '@vivli/core/infrastructure/context';
import Tippy from '@tippyjs/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';

const citationStyleGeneric: CSSProperties = {
    fontSize: Size.FontSize.Medium,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    flexWrap: 'wrap',
    color: Color.DARK_GRAY,
    marginRight: '10px',
    backgroundColor: Color.TRANSPARENT,
    marginBottom: '-20px',
};

const citationBodyStyle: CSSProperties = {
    ...citationStyleGeneric,
    marginTop: '-5px',
    width: '100%',
};

const citationJournalStyle: CSSProperties = {
    ...citationStyleGeneric,
    fontStyle: 'italic',
    marginRight: '0px',
};

const citationTitleStyle: CSSProperties = {
    ...citationStyleGeneric,
    marginRight: '0px',
    minWidth: '0px',
    maxWidth: '100%',
    whiteSpace: 'normal',
    lineHeight: '13px',
    marginTop: '10px',
    marginBottom: '10px',
};

const iconStyle: CSSProperties = {
    fontSize: '1.5em',
    padding: '0px 0px 0px 0px',
    color: Color.TEXT_LIGHT_BLUE,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    cursor: 'pointer',
};

const twoColumnGrid: CSSProperties = {
    width: '100%',
    display: 'grid',
    justifyContent: 'right',
    gridTemplateColumns: '35fr 1fr',
    columnGap: '10px',
};

interface CitationViewProps {
    citation: ICitation;
}

export const CitationView = ({ citation }: CitationViewProps) => {
    const toastService = useToastApiContext();
    const { formattedAuthors, remainderFormatted, formattedDoi, noDoiCitation, formattedJournalText, formattedTitleText } =
        useCitation(citation);
    const formattedAuthorsString = formattedAuthors(false);
    const remainderFormattedString = remainderFormatted(false);
    const formattedDoiString = formattedDoi();
    const entireCitation = `${formattedAuthorsString} ${formattedTitleText} ${formattedJournalText}  ${remainderFormattedString} ${formattedDoiString}`;

    const handleCopyToClipboard = (value) => {
        navigator.clipboard
            .writeText(value)
            .then(() => toastService.success('Copied!'))
            .catch(() => toastService.error('Failed to copy:'));
    };

    return (
        <div style={twoColumnGrid}>
            <div style={{ overflowX: 'auto' }}>
                <Tippy content={formattedAuthorsString}>
                    <div style={citationBodyStyle}>{formattedAuthorsString}</div>
                </Tippy>
                <div style={citationTitleStyle}>{formattedTitleText}</div>
                <div style={{ marginTop: '-20px' }}>
                    <span style={citationJournalStyle}>{formattedJournalText}</span>
                    <span style={citationStyleGeneric}>
                        {remainderFormattedString}
                        {!noDoiCitation && (
                            <a href={formattedDoiString} target="_blank">
                                doi: {formattedDoiString}
                            </a>
                        )}
                        {noDoiCitation && (
                            <a href={citation?.url} target="_blank">
                                url: {citation?.url}
                            </a>
                        )}
                    </span>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <FontAwesomeIcon onClick={() => handleCopyToClipboard(entireCitation)} style={iconStyle} icon={faCopy} />
            </div>
        </div>
    );
};
